/**
 * Games as a video-ad subject, sourced from the config-driven DATA SOURCES (not the dsi site bridge).
 *
 * Shape (verified against the live data-source API):
 *  - `all_slot_games_api` (tenant-0 GUEST catalogue) resolves to FULL game objects: `id`, `name`,
 *    `provider_id`, `url`, and imagery under `art_bundle.data` (variant-keyed CDN URLs). NOTE: this is the
 *    guest id; `window.config.dataSourceAllGames` is the PLAYER-scoped variant, which a guest token cannot
 *    read, so the guest catalogue id is used directly.
 *  - `spk-most-popular` / `spk-top-games` are CURATED ORDERINGS: their data is an array of game-id strings,
 *    resolved to full games against the catalogue.
 * Fetched server-side through spark-creatives (`games-api.ts` -> GET /creatives/tenant/{id}/data-source),
 * NOT the config-engine `dataSources` slice: the browser can't read these with the guest token on prod
 * (401 + owner sign-out). See the spec (`.claude/specs/2026-09-23-video-ad-subjects.md`).
 */
import type { StoryboardPromotion, VideoAspectRatio } from '@/store/slices/creativeJobs';

import type { CreativeItem } from './creative-api';
import type { VideoSubject } from './video-subjects';

/** The three game browse tabs, in display order. */
export type GameTab = 'popular' | 'top' | 'all';
export const GAME_TABS: readonly GameTab[] = ['popular', 'top', 'all'];

/** The tenant-0 guest catalogue (full game objects). The curated tabs are resolved against it. */
export const GAME_CATALOG_SOURCE_ID = 'all_slot_games_api';

/** The curated ordering source (an array of game-id strings) backing a Popular / Top tab. */
export const gameListSourceId = (tab: 'popular' | 'top'): string =>
  tab === 'popular' ? 'spk-most-popular' : 'spk-top-games';

/**
 * One game record from the catalogue. Loose on purpose (the source serves many fields); only what we read
 * is typed. Imagery is under `art_bundle.data`, variant-keyed.
 *
 * Two shapes: a FULL record (art bundle, url, every attribute) and a SLIM one, what `useGameSources` holds (id, name,
 * provider_id and the type / subtype / visual-theme attributes only). The art helpers below find nothing on a slim
 * record: read the full one by id (`games-api` fetchGamesByIds, `useGameRecords`, `useGameThumb`, `GameThumb`).
 */
export interface GameSourceRecord {
  id?: string | number;
  name?: string;
  provider_id?: string | number;
  /** The game's on-site path (e.g. `/slot-game/786/…`). */
  url?: string;
  art_bundle?: { data?: Record<string, unknown> };
  [key: string]: unknown;
}

/** A game's stable id as a string (the subject id). */
export const gameKey = (game: GameSourceRecord): string => String(game.id ?? '');

/** A game's display title (its name, else `#<id>`). */
export const gameTitle = (game: GameSourceRecord): string => game.name?.trim() || `#${gameKey(game)}`;

/** The first http(s) URL among `keys` in the game's `art_bundle.data`, else any http URL there. Spaces are
 *  encoded (CDN paths embed the game name). Undefined when the game carries no usable art. */
const artUrl = (game: GameSourceRecord, keys: readonly string[]): string | undefined => {
  const data = game.art_bundle?.data;
  if (!data) return undefined;
  const isHttp = (v: unknown): v is string => typeof v === 'string' && /^https?:\/\//.test(v);
  const enc = (v: string): string => v.replace(/ /g, '%20');
  for (const key of keys) {
    if (isHttp(data[key])) return enc(data[key] as string);
  }
  const any = Object.values(data).find(isHttp);
  return any ? enc(any) : undefined;
};

/** Variant keys, best-first, per role. */
const SQUARE = 'square_ar11_var_keycenter';
const PORTRAIT = 'portrait_ar23_var_keycenter';
const LANDSCAPE = 'landscape_ar32_var_keycenter';

/** Square-ish thumbnail for the picker grid. */
export const gameThumbUrl = (game: GameSourceRecord): string | undefined =>
  artUrl(game, [SQUARE, PORTRAIT, LANDSCAPE]);

/** The art variant best matching the ad's aspect, for the storyboard key visual; falls back to the thumb. */
const ASPECT_ART_KEYS: Record<VideoAspectRatio, readonly string[]> = {
  '16:9': [LANDSCAPE, SQUARE, PORTRAIT],
  '9:16': [PORTRAIT, SQUARE, LANDSCAPE],
  '1:1': [SQUARE, LANDSCAPE, PORTRAIT],
  '4:5': [PORTRAIT, SQUARE, LANDSCAPE],
  '2:3': [PORTRAIT, SQUARE, LANDSCAPE],
};
export const gameKeyArtUrl = (game: GameSourceRecord, aspect: VideoAspectRatio): string | undefined =>
  artUrl(game, ASPECT_ART_KEYS[aspect] ?? [SQUARE, PORTRAIT, LANDSCAPE]) ?? gameThumbUrl(game);

/** The catalogue's game array (full objects), tolerant of the untyped slice shape. */
export const gamesOfSource = (source: unknown): GameSourceRecord[] => {
  const data = (source as { data?: unknown } | undefined)?.data;
  return Array.isArray(data) ? (data as GameSourceRecord[]) : [];
};

/** A curated source's ordered game-id strings (its data is `['3162', …]`; tolerant of numbers/objects). */
export const gameIdsOfSource = (source: unknown): string[] => {
  const data = (source as { data?: unknown } | undefined)?.data;
  if (!Array.isArray(data)) return [];
  return data
    .map((x) => (typeof x === 'string' || typeof x === 'number' ? String(x) : String((x as GameSourceRecord)?.id ?? '')))
    .filter(Boolean);
};

/** Resolve an ordered id list to catalogue games, preserving order and skipping ids not in the catalogue. */
export const resolveGamesByIds = (ids: readonly string[], catalog: readonly GameSourceRecord[]): GameSourceRecord[] => {
  const byId = new Map(catalog.map((g) => [gameKey(g), g]));
  return ids.map((id) => byId.get(id)).filter((g): g is GameSourceRecord => Boolean(g));
};

/**
 * Adapt a game to a normalised {@link VideoSubject}. No on-site cta_href for a game, so `destinationUrl` is
 * left undefined and the tracking link falls back to the brand homepage (per the spec's destination rule).
 */
export const gameSubjectFromRecord = (game: GameSourceRecord, aspect: VideoAspectRatio): VideoSubject => ({
  subject_type: 'game',
  id: gameKey(game),
  image_url: gameKeyArtUrl(game, aspect) ?? '',
  title: gameTitle(game),
});

/**
 * Adapt a game to the composer's {@link CreativeItem} shape so a game ad reuses the existing storyboard
 * flow: the game id stands in for `banner_id`, the aspect-matched art is the key visual, the name is the
 * headline.
 */
export const creativeItemFromGame = (game: GameSourceRecord, aspect: VideoAspectRatio): CreativeItem => ({
  banner_id: gameKey(game),
  image_url: gameKeyArtUrl(game, aspect) ?? '',
  headline: gameTitle(game),
  internal_name: gameTitle(game),
});

/** Adapt a game to a {@link StoryboardPromotion} for pre-seeding a game ad's draft storyboard. Kept in
 *  lock-step with {@link creativeItemFromGame} (same id + key art) so the composer matches the seeded
 *  promotion back to its pool item on reopen. */
export const storyboardPromotionFromGame = (game: GameSourceRecord, aspect: VideoAspectRatio): StoryboardPromotion => ({
  banner_id: gameKey(game),
  image_url: gameKeyArtUrl(game, aspect) ?? '',
  headline: gameTitle(game),
});
